import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { Storage } from 'aws-amplify';

interface ReportAuthor {
  name: string;
  title: string;
  email: string;
  phone: string;
}

interface ReportCompany {
  name: string;
  address: string;
  email: string;
  phone: string;
  logo: string;
}

interface ReportProperty {
  name: string;
  age: string;
  address: string;
  city: string;
}

interface ReportMainImage {
  url: string;
  alt: string;
}

type BlockType =
  | 'heading'
  | 'paragraph'
  | 'image'
  | 'image_gallery'
  | 'bullet_list'
  | 'numbered_list'
  | 'divider'
  | 'spacer';

interface ContentBlock {
  id: string;
  type: BlockType;
  text?: string;
  level?: 1 | 2 | 3;
  alignment?: 'left' | 'center' | 'right';
  bold?: boolean;
  italic?: boolean;
  url?: string;
  caption?: string;
  width?: number;
  images?: Array<{ url: string; caption?: string }>;
  columns?: 1 | 2 | 3 | 4;
  items?: Array<{ text: string; bold?: boolean }>;
  style?: 'solid' | 'dashed' | 'dotted';
  height?: number;
}

interface ReportSection {
  order: number;
  blocks: ContentBlock[];
}

interface ReportData {
  dealId: string;
  title: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  author: ReportAuthor;
  company: ReportCompany;
  property: ReportProperty;
  mainImage: ReportMainImage;
  sections: ReportSection[];
}

interface ReportViewModalProps {
  open: boolean;
  onClose: () => void;
  reportData: ReportData | null;
  onShare?: () => void;
}

const ReportViewModal: React.FC<ReportViewModalProps> = ({
  open,
  onClose,
  reportData,
  onShare,
}) => {
  const { theme } = useTheme();

  const [mainImageUrl, setMainImageUrl] = React.useState<string>('');
  const [logoUrl, setLogoUrl] = React.useState<string>('');
  const [blockImageUrls, setBlockImageUrls] = React.useState<
    Record<string, string>
  >({});

  React.useEffect(() => {
    const loadImages = async () => {
      if (!reportData) return;

      if (reportData.mainImage?.url) {
        try {
          const url = await Storage.get(reportData.mainImage.url);
          setMainImageUrl(url);
        } catch (e) {
          console.error('Error loading main image:', e);
        }
      }

      if (reportData.company?.logo) {
        try {
          const url = await Storage.get(reportData.company.logo);
          setLogoUrl(url);
        } catch (e) {
          console.error('Error loading logo:', e);
        }
      }

      const imageBlocks: Record<string, string> = {};
      for (const section of reportData.sections || []) {
        for (const block of section.blocks || []) {
          if (block.type === 'image' && block.url) {
            try {
              const url = await Storage.get(block.url);
              imageBlocks[block.id] = url;
            } catch (e) {
              console.error('Error loading block image:', e);
            }
          }
          if (block.type === 'image_gallery' && block.images) {
            for (let i = 0; i < block.images.length; i++) {
              const img = block.images[i];
              if (img.url) {
                try {
                  const url = await Storage.get(img.url);
                  imageBlocks[`${block.id}-${i}`] = url;
                } catch (e) {
                  console.error('Error loading gallery image:', e);
                }
              }
            }
          }
        }
      }
      setBlockImageUrls(imageBlocks);
    };

    loadImages();
  }, [reportData]);

  const sortedSections = useMemo(() => {
    if (!reportData?.sections?.length) return [];
    return [...reportData.sections]
      .filter((section) => section.blocks && section.blocks.length > 0)
      .sort((a, b) => a.order - b.order);
  }, [reportData?.sections]);

  if (!reportData) return null;

  const renderCoverPage = () => (
    <Box
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {mainImageUrl && (
        <Box
          component="img"
          src={mainImageUrl}
          alt={reportData.mainImage?.alt || 'Cover'}
          sx={{
            width: '100%',
            height: 'auto',
            objectFit: 'cover',
          }}
        />
      )}
      {/* <Box sx={{ p: 3 }}>
        <Typography
          sx={{ fontSize: 24, fontWeight: 700, color: theme.text, mb: 1 }}
        >
          {reportData.title || 'Damage Report'}
        </Typography>
        <Typography sx={{ fontSize: 14, color: theme.textLight, mb: 2 }}>
          {new Date(reportData.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Typography>
        {reportData.version && (
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: `${theme.primary}20`,
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 12, color: theme.primary }}>
              Version {reportData.version}
            </Typography>
          </Box>
        )}
      </Box> */}
    </Box>
  );

  const renderAuthorInfo = () => {
    if (!reportData.author?.name) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Avatar sx={{ width: 48, height: 48, backgroundColor: theme.primary }}>
          {reportData.author.name
            .split(' ')
            .map((n) => n[0])
            .join('')}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, color: theme.text }}>
            {reportData.author.name}
          </Typography>
          {reportData.author.title && (
            <Typography sx={{ fontSize: 14, color: theme.textLight }}>
              {reportData.author.title}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            {reportData.author.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 14, color: theme.textLight }} />
                <Typography sx={{ fontSize: 12, color: theme.textLight }}>
                  {reportData.author.email}
                </Typography>
              </Box>
            )}
            {reportData.author.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: theme.textLight }} />
                <Typography sx={{ fontSize: 12, color: theme.textLight }}>
                  {reportData.author.phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        {logoUrl && (
          <Box
            component="img"
            src={logoUrl}
            alt="Company Logo"
            sx={{ height: 40, objectFit: 'contain' }}
          />
        )}
      </Box>
    );
  };

  const renderPropertySummary = () => {
    const fields = [
      { label: 'Property Name', value: reportData.property?.name },
      { label: 'Year Built', value: reportData.property?.age },
      { label: 'Address', value: reportData.property?.address },
      { label: 'City', value: reportData.property?.city },
    ].filter((f) => f.value && f.value.trim().length);

    if (!fields.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{ fontSize: 16, fontWeight: 600, color: theme.text, mb: 2 }}
        >
          About the Property
        </Typography>
        {fields.map((field) => (
          <Box
            key={field.label}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              py: 0.75,
              borderBottom: `1px solid ${theme.stroke}`,
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Typography sx={{ fontSize: 14, color: theme.textLight }}>
              {field.label}
            </Typography>
            <Typography sx={{ fontSize: 14, color: theme.text }}>
              {field.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const renderBlock = (block: ContentBlock) => {
    switch (block.type) {
      case 'heading':
        const headingSize =
          block.level === 1 ? 20 : block.level === 2 ? 18 : 16;
        return (
          <Typography
            key={block.id}
            sx={{
              fontSize: headingSize,
              fontWeight: 600,
              color: theme.text,
              mb: 1,
              textAlign: block.alignment || 'left',
            }}
          >
            {block.text}
          </Typography>
        );

      case 'paragraph':
        return (
          <Typography
            key={block.id}
            sx={{
              fontSize: 14,
              color: theme.text,
              mb: 1.5,
              fontWeight: block.bold ? 600 : 400,
              fontStyle: block.italic ? 'italic' : 'normal',
              textAlign: block.alignment || 'left',
              whiteSpace: 'pre-wrap',
            }}
          >
            {block.text}
          </Typography>
        );

      case 'image':
        const imgUrl = blockImageUrls[block.id];
        if (!imgUrl) return null;
        return (
          <Box key={block.id} sx={{ mb: 2 }}>
            <Box
              component="img"
              src={imgUrl}
              alt={block.caption || ''}
              sx={{
                width: block.width ? `${block.width}%` : '100%',
                borderRadius: 1,
                objectFit: 'cover',
              }}
            />
            {block.caption && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: theme.textLight,
                  mt: 0.5,
                  textAlign: 'center',
                }}
              >
                {block.caption}
              </Typography>
            )}
          </Box>
        );

      case 'image_gallery':
        const columns = block.columns || 2;
        return (
          <Box
            key={block.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap: 1,
              mb: 2,
            }}
          >
            {block.images?.map((img, idx) => {
              const galUrl = blockImageUrls[`${block.id}-${idx}`];
              if (!galUrl) return null;
              return (
                <Box key={idx}>
                  <Box
                    component="img"
                    src={galUrl}
                    alt={img.caption || ''}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: 1,
                    }}
                  />
                  {img.caption && (
                    <Typography
                      sx={{ fontSize: 10, color: theme.textLight, mt: 0.5 }}
                    >
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );

      case 'bullet_list':
        return (
          <Box key={block.id} component="ul" sx={{ pl: 2, mb: 1.5, mt: 0 }}>
            {block.items?.map((item, idx) => (
              <Typography
                key={idx}
                component="li"
                sx={{
                  fontSize: 14,
                  color: theme.text,
                  fontWeight: item.bold ? 600 : 400,
                }}
              >
                {item.text}
              </Typography>
            ))}
          </Box>
        );

      case 'numbered_list':
        return (
          <Box key={block.id} component="ol" sx={{ pl: 2, mb: 1.5, mt: 0 }}>
            {block.items?.map((item, idx) => (
              <Typography
                key={idx}
                component="li"
                sx={{
                  fontSize: 14,
                  color: theme.text,
                  fontWeight: item.bold ? 600 : 400,
                }}
              >
                {item.text}
              </Typography>
            ))}
          </Box>
        );

      case 'divider':
        return (
          <Divider
            key={block.id}
            sx={{
              my: 2,
              borderStyle: block.style || 'solid',
              borderColor: theme.stroke,
            }}
          />
        );

      case 'spacer':
        return <Box key={block.id} sx={{ height: block.height || 16 }} />;

      default:
        return null;
    }
  };

  const renderSection = (section: ReportSection, index: number) => (
    <Box
      key={`section-${section.order}-${index}`}
      sx={{
        backgroundColor: theme.background2,
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      {section.blocks.map((block) => renderBlock(block))}
    </Box>
  );

  const renderFooter = () => {
    if (!reportData.company?.name) return null;

    return (
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
          mt: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
          {reportData.company.name}
        </Typography>
        {reportData.company.address && (
          <Typography sx={{ fontSize: 12, color: theme.textLight, mt: 0.5 }}>
            {reportData.company.address}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 1,
          }}
        >
          {reportData.company.phone && (
            <Typography sx={{ fontSize: 12, color: theme.textLight }}>
              {reportData.company.phone}
            </Typography>
          )}
          {reportData.company.email && (
            <Typography sx={{ fontSize: 12, color: theme.textLight }}>
              {reportData.company.email}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      // Above the NavBar (z-index 9999), which would cover the close button
      sx={{ zIndex: 100000 }}
      PaperProps={{
        sx: {
          backgroundColor: theme.background1,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: `1px solid ${theme.stroke}`,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
          Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onShare && (
            <IconButton onClick={onShare} sx={{ color: theme.text }}>
              <ShareIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose} sx={{ color: theme.text }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ backgroundColor: theme.background3, p: 2 }}>
        {/* Same reading width as the v2 report view */}
        <Box sx={{ maxWidth: 880, mx: 'auto' }}>
          {renderCoverPage()}
          {renderAuthorInfo()}
          {renderPropertySummary()}
          {sortedSections.map((section, idx) => renderSection(section, idx))}
          {renderFooter()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ReportViewModal;
